import React, { useEffect, useState } from 'react';
import './RevenueAnalytics.css';
import { Box, Typography, Grid } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import RevenueLineChart from './RevenueLineChart/RevenueLineChart';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { CardSkeleton, ChartSkeleton } from '../../SkeletonLoader/SkeletonLoader';
import { settledData } from '../AnalyticsCard/AnalyticsCard';

const RevenueAnalytics = ({ timePeriod = 'last_month' }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [lifetimeData, setLifetimeData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Map component timePeriod to API period values
  const getApiPeriod = timePeriod => {
    switch (timePeriod) {
      case 'last_day':
        return 'today';
      case 'last_week':
        return '1_week';
      case 'last_2_weeks':
        return '2_weeks';
      case 'last_month':
        return '1_month';
      case 'last_3_months':
        return '3_months';
      case 'last_6_months':
        return '6_months';
      case 'last_year':
        return '1_year';
      default:
        return '1_month';
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchAnalytics = async () => {
      try {
        setLoading(true);

        const apiPeriod = getApiPeriod(timePeriod);

        // allSettled: if one request fails, only its cards show "--".
        // (The revenue chart fetches its own data, so no chart request here.)
        const [revenueResponse, lifetimeResponse] = await Promise.allSettled([
          axiosInstance.get(`/admin/dashboard/revenue-metrics?period=${apiPeriod}`),
          axiosInstance.get(`/admin/dashboard/lifetime-value-metrics`),
        ]);

        if (isMounted) {
          setAnalyticsData(settledData(revenueResponse, 'revenue metrics'));
          setLifetimeData(settledData(lifetimeResponse, 'lifetime value metrics'));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchAnalytics();
    return () => {
      isMounted = false;
    };
  }, [timePeriod]);

  const formatCurrency = amount => {
    if (amount === undefined || amount === null || amount === '--') return '--';
    const num = parseFloat(amount);
    if (isNaN(num)) return '--';
    return `${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Riyal`;
  };

  // Get time period specific data
  const getTimeSpecificData = () => {
    switch (timePeriod) {
      case 'last_day':
        return {
          title: 'Daily Revenue',
          profitTitle: 'Daily Profit',
          avgTitle: 'Average Daily Revenue/User',
          comparisonPeriod: 'previous day',
        };
      case 'last_week':
        return {
          title: 'Weekly Revenue',
          profitTitle: 'Weekly Profit',
          avgTitle: 'Average Weekly Revenue/User',
          comparisonPeriod: 'previous week',
        };
      case 'last_2_weeks':
        return {
          title: '2-Week Revenue',
          profitTitle: '2-Week Profit',
          avgTitle: 'Average 2-Week Revenue/User',
          comparisonPeriod: 'previous 2 weeks',
        };
      case 'last_month':
        return {
          title: 'Monthly Revenue',
          profitTitle: 'Monthly Profit',
          avgTitle: 'Average Monthly Revenue/User',
          comparisonPeriod: 'last month',
        };
      case 'last_3_months':
        return {
          title: '3-Month Revenue',
          profitTitle: '3-Month Profit',
          avgTitle: 'Average 3-Month Revenue/User',
          comparisonPeriod: 'last 3 months',
        };
      case 'last_6_months':
        return {
          title: '6-Month Revenue',
          profitTitle: '6-Month Profit',
          avgTitle: 'Average 6-Month Revenue/User',
          comparisonPeriod: 'last 6 months',
        };
      case 'last_year':
        return {
          title: 'Yearly Revenue',
          profitTitle: 'Yearly Profit',
          avgTitle: 'Average Yearly Revenue/User',
          comparisonPeriod: 'previous year',
        };
      default:
        return {
          title: 'Monthly Revenue',
          profitTitle: 'Monthly Profit',
          avgTitle: 'Average Monthly Revenue/User',
          comparisonPeriod: 'last month',
        };
    }
  };

  const timeData = getTimeSpecificData();

  // Card data mapping using new API structure
  const cardData = [
    {
      title: timeData.title,
      value: formatCurrency(analyticsData?.total_revenue?.value),
      change: analyticsData?.total_revenue?.growth_percentage,
      fromZero: analyticsData?.total_revenue?.growth_percentage === null,
      rawValue: analyticsData?.total_revenue?.value,
      unavailable: !analyticsData,
      period: timeData.comparisonPeriod,
    },
    {
      title: timeData.profitTitle,
      value: formatCurrency(analyticsData?.total_profit?.value),
      change: analyticsData?.total_profit?.growth_percentage,
      fromZero: analyticsData?.total_profit?.growth_percentage === null,
      rawValue: analyticsData?.total_profit?.value,
      unavailable: !analyticsData,
      period: timeData.comparisonPeriod,
    },
    {
      title: timeData.avgTitle,
      value: formatCurrency(analyticsData?.avg_revenue_per_user?.value),
      change: analyticsData?.avg_revenue_per_user?.growth_percentage,
      fromZero: analyticsData?.avg_revenue_per_user?.growth_percentage === null,
      rawValue: analyticsData?.avg_revenue_per_user?.value,
      unavailable: !analyticsData,
      period: timeData.comparisonPeriod,
    },
    {
      title: 'Average Profit/User',
      value: formatCurrency(analyticsData?.avg_profit_per_user?.value),
      change: analyticsData?.avg_profit_per_user?.growth_percentage,
      fromZero: analyticsData?.avg_profit_per_user?.growth_percentage === null,
      rawValue: analyticsData?.avg_profit_per_user?.value,
      unavailable: !analyticsData,
      period: timeData.comparisonPeriod,
    },
    {
      title: 'Instructor Earnings (All Time)',
      value: formatCurrency(lifetimeData?.instructor_lifetime_value),
      change: null, // No growth rate for lifetime values
      period: null,
      unavailable: !lifetimeData,
    },
    {
      title: 'Learner Payments, Net of Refunds (All Time)',
      value: formatCurrency(lifetimeData?.learner_lifetime_value),
      change: null, // No growth rate for lifetime values
      period: null,
      unavailable: !lifetimeData,
    },
  ];

  // Card UI
  // fromZero: the previous period was 0 (API sends growth_percentage: null), so a
  // percentage change is undefined; show "from 0" instead.
  // unavailable: this card's request failed, so show "--" rather than a fake 0.
  const AnalyticsCard = ({
    title,
    value,
    change,
    period,
    fromZero = false,
    rawValue,
    unavailable = false,
  }) => (
    <div className="revenueanalytics-card">
      <div className="revenueanalytics-card-title">{title}</div>
      <div className="revenueanalytics-card-value">{unavailable ? '--' : value}</div>
      {!unavailable && fromZero && period !== null && (
        <div
          className={`revenueanalytics-card-change ${rawValue < 0 ? 'revenueanalytics-negative-change' : 'revenueanalytics-positive-change'}`}
        >
          {rawValue < 0 ? <TrendingDown fontSize="small" /> : <TrendingUp fontSize="small" />}
          from 0 {period}
        </div>
      )}
      {!unavailable && !fromZero && change !== null && period !== null && (
        <div
          className={`revenueanalytics-card-change ${parseFloat(change) >= 0 ? 'revenueanalytics-positive-change' : 'revenueanalytics-negative-change'}`}
          style={{ opacity: change === '--' ? 0.5 : 1 }}
        >
          {change === '--' ? (
            '--'
          ) : parseFloat(change) >= 0 ? (
            <TrendingUp fontSize="small" />
          ) : (
            <TrendingDown fontSize="small" />
          )}
          {change === '--' ? '--' : `${Math.abs(change).toFixed(1)}%`} vs {period}
        </div>
      )}
    </div>
  );

  return (
    <Box className="revenueanalytics-section">
      <Typography className="revenueanalytics-section-title-revenue">Revenue Analytics</Typography>
      <Grid container spacing={2} className="revenueanalytics-cards">
        {cardData.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} key={card.title}>
            {loading ? <CardSkeleton /> : <AnalyticsCard {...card} />}
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2} className="revenueanalytics-charts">
        <Grid item xs={12}>
          {loading ? <ChartSkeleton /> : <RevenueLineChart />}
        </Grid>
      </Grid>
    </Box>
  );
};

export default RevenueAnalytics;
